<template>
  <el-dialog
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    :title="dialogTitle"
    :visible.sync="dialogVisible"
    top="5vh"
    width="650px"
    @close="handleClose"
  >
    <el-form
      ref="formRef" :model="form" :rules="rules"
      label-position="top"
    >
      <el-form-item :label="$t('podInvalidKeyword.invalidType')" prop="keywordType">
        <el-select
          v-model="form.keywordType"
          :placeholder="$t('podInvalidKeyword.pleaseSelect')"
          style="width: 100%"
        >
          <el-option
            v-for="item in typeOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item :label="$t('podInvalidKeyword.nameZh')" prop="typeLabel">
        <el-input
          v-model="form.typeLabel"
          :placeholder="$t('podInvalidKeyword.nameZhPlaceholder')"
          maxlength="60"
        />
      </el-form-item>
      <el-form-item :label="$t('podInvalidKeyword.namePu')" prop="typeLabelPu">
        <el-input
          v-model="form.typeLabelPu"
          :placeholder="$t('podInvalidKeyword.namePuPlaceholder')"
          maxlength="100"
        />
      </el-form-item>
      <el-form-item :label="$t('podInvalidKeyword.keywords')" prop="keywords">
        <div class="keyword-box">
          <el-tag
            v-for="(keyword, index) in form.keywords"
            :key="index"
            class="keyword-tag"
            closable
            size="small"
            @close="removeKeyword(index)"
          >{{ keyword }}
          </el-tag>
          <el-input
            v-model="keywordInput"
            :placeholder="$t('podInvalidKeyword.keywordPlaceholder')"
            class="keyword-input"
            maxlength="100"
            @blur="addKeywords(keywordInput)"
            @keydown.native="handleKeywordKeydown"
          />
        </div>
        <div class="keyword-tip">
          {{ $t('podInvalidKeyword.keywordTip') }}
        </div>
      </el-form-item>
    </el-form>
    <span slot="footer" class="dialog-footer">
      <el-button @click="dialogVisible = false">
        {{ $t('podInvalidKeyword.cancel') }}
      </el-button>
      <el-button type="primary" @click="submitForm">
        {{ $t('podInvalidKeyword.confirm') }}
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
import { podInvalidKeywordSave } from '@/api/resource/podInvalidKeyword';

// 关键词分隔符：换行 / 回车 / 中英文逗号 / 顿号 / 中英文分号 / 竖线（与后端拆分口径保持一致）
const KEYWORD_SEPARATOR = /[\n\r,，、;；|]+/;

// 表单默认值
function getDefaultForm() {
  return {
    id: null,
    keywordType: 2,
    typeLabel: '',
    typeLabelPu: '',
    keywords: []
  };
}

export default {
  name: 'PODInvalidKeywordDialog',
  props: {
    // 弹窗显隐，配合 .sync 使用
    visible: {
      type: Boolean,
      default: false
    },
    // 编辑时传入列表行数据，新增传 null
    editRow: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      keywordInput: '',
      form: getDefaultForm()
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(val) {
        this.$emit('update:visible', val);
      }
    },
    dialogTitle() {
      return this.isEdit
        ? this.$t('podInvalidKeyword.editCategory')
        : this.$t('podInvalidKeyword.addCategory');
    },
    isEdit() {
      return !!this.editRow;
    },
    // 不合格类型：1-签名，2-图片
    typeOptions() {
      return [
        { label: this.$t('podInvalidKeyword.typePhoto'), value: 2 },
        { label: this.$t('podInvalidKeyword.typeSignature'), value: 1 }
      ];
    },
    rules() {
      return {
        keywordType: [
          { required: true, message: this.$t('podInvalidKeyword.typeRequired'), trigger: 'change' }
        ],
        typeLabel: [
          {
            required: true,
            whitespace: true,
            message: this.$t('podInvalidKeyword.nameZhRequired'),
            trigger: ['blur', 'change']
          }
        ],
        typeLabelPu: [
          {
            required: true,
            whitespace: true,
            message: this.$t('podInvalidKeyword.namePuRequired'),
            trigger: ['blur', 'change']
          }
        ],
        keywords: [
          {
            validator: (rule, value, callback) => {
              if (!value || !value.length) {
                callback(new Error(this.$t('podInvalidKeyword.keywordRequired')));
                return;
              }
              callback();
            },
            trigger: 'change'
          }
        ]
      };
    }
  },
  watch: {
    visible(val) {
      if (val) this.initForm();
    }
  },
  methods: {
    // 打开弹窗时初始化表单
    initForm() {
      this.keywordInput = '';
      if (this.editRow) {
        this.form = {
          id: this.editRow.id,
          keywordType: this.editRow.keywordType,
          typeLabel: this.editRow.typeLabel,
          typeLabelPu: this.editRow.typeLabelPu,
          keywords: this.splitKeywords(this.editRow.keywords)
        };
      } else {
        this.form = getDefaultForm();
      }
      this.$nextTick(() => {
        if (this.$refs.formRef) this.$refs.formRef.clearValidate();
      });
    },
    // 关键词串 -> 数组：按后端同一套分隔符拆分，去空、去重
    splitKeywords(value) {
      if (!value) return [];
      const result = [];
      String(value)
        .split(KEYWORD_SEPARATOR)
        .map((item) => item.trim())
        .filter(Boolean)
        .forEach((item) => {
          const exists = result.some((keyword) => keyword.toLowerCase() === item.toLowerCase());
          if (!exists) result.push(item);
        });
      return result;
    },
    // 添加关键词：输入 / 粘贴内容统一按分隔符拆成多个 tag
    addKeywords(raw) {
      const list = String(raw || '')
        .slice(0, 100)
        .split(KEYWORD_SEPARATOR)
        .map((item) => item.trim())
        .filter(Boolean);
      let changed = false;
      list.forEach((item) => {
        const exists = this.form.keywords.some(
          (keyword) => keyword.toLowerCase() === item.toLowerCase()
        );
        if (!exists) {
          this.form.keywords.push(item);
          changed = true;
        }
      });
      this.keywordInput = '';
      if (changed) this.validateKeywordsField();
    },
    removeKeyword(index) {
      this.form.keywords.splice(index, 1);
      this.validateKeywordsField();
    },
    handleKeywordKeydown(event) {
      if (event.key === 'Enter' || event.key === ',') {
        event.preventDefault();
        this.addKeywords(this.keywordInput);
      }
    },
    validateKeywordsField() {
      this.$nextTick(() => {
        if (this.$refs.formRef) this.$refs.formRef.validateField('keywords');
      });
    },
    handleClose() {
      this.keywordInput = '';
      if (this.$refs.formRef) this.$refs.formRef.clearValidate();
    },
    submitForm() {
      // 提交前把输入框里残留的内容也转成 tag
      this.addKeywords(this.keywordInput);
      this.$refs.formRef.validate((valid) => {
        if (!valid) return;
        const payload = {
          keywordType: this.form.keywordType,
          typeLabel: (this.form.typeLabel || '').trim(),
          typeLabelPu: (this.form.typeLabelPu || '').trim(),
          keywords: this.form.keywords.join('\n')
        };
        if (this.isEdit) payload.id = this.form.id;
        podInvalidKeywordSave(payload).then((res) => {
          if (res.code === 200) {
            this.$message.success(
              this.isEdit
                ? this.$t('podInvalidKeyword.editSuccess')
                : this.$t('podInvalidKeyword.addSuccess')
            );
            this.dialogVisible = false;
            // 通知父组件刷新列表
            this.$emit('success');
          }
        });
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.keyword-tag {
  // 超长关键词（如无空格的连续字符）限制在容器宽度内换行，避免撑破弹窗
  flex: 0 1 auto;
  max-width: 100%;
  height: auto;
  margin: 2px 5px 2px 0;
  padding: 2px 8px;
  line-height: 18px;
  white-space: normal;
  word-break: break-all;
}

.keyword-box {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  box-sizing: border-box;
  width: 100%;
  min-height: 40px;
  padding: 2px 8px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  transition: border-color 0.2s;

  &:hover {
    border-color: #c0c4cc;
  }

  .keyword-input {
    flex: 1;
    min-width: 140px;

    ::v-deep .el-input__inner {
      height: 30px;
      line-height: 30px;
      padding: 0;
      border: 0;
      background: transparent;
    }
  }
}

.keyword-tip {
  margin-top: 4px;
  font-size: 12px;
  line-height: 18px;
  color: #909399;
}
</style>
